// Local re-implementation of the znk-be game rules (models/game/Game.java, Team.java),
// so a game can be played with 3 bots and no server/websocket connection.

import { CardDto, ThrowCardResponse } from '../../message-handler/types/GameMessage';

export type { CardDto };
export type PlayerEffect = 'APPEND' | 'ZNK' | 'SLICE';
export type RoundState = 'TURN_FINISHED' | 'ROUND_FINISHED' | 'GAME_OVER';

export interface TeamScore {
  score: number;
  totalCards: number;
  znkCount: number;
  has2ofClub: boolean;
  has10ofDiamond: boolean;
}

export interface GameScore {
  team1Score: TeamScore;
  team2Score: TeamScore;
  gameId: string;
  winner: string;
}

interface ScoredPile {
  round: number;
  turn: number;
  cards: CardDto[];
}

export interface OfflineGameState {
  gameId: string;
  team1: [string, string];
  team2: [string, string];
  // cross-team rotation: T1P1, T2P1, T1P2, T2P2
  turnOrder: [string, string, string, string];
  currentTurnIndex: number;
  round: number;
  roundTurn: number;
  middle: CardDto[];
  hands: Record<string, CardDto[][]>;
  remainingHand: Record<string, CardDto[]>;
  scoredPiles: { team1: ScoredPile[]; team2: ScoredPile[] };
}

const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const SUITS = ['club', 'diamond', 'heart', 'spade'];
const TOTAL_ROUNDS = 3;
const TURNS_PER_ROUND = 16;

const buildShuffledDeck = (): CardDto[] => {
  const deck: CardDto[] = [];
  for (const rank of RANKS) {
    for (const suit of SUITS) {
      deck.push({ rank, suit });
    }
  }
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
};

const randomRotation = (): number => Math.floor(Math.random() * 71) - 35;

export const withRotation = (card: CardDto): CardDto & { rotate: number } => ({ ...card, rotate: randomRotation() });

export const createOfflineGame = (
  gameId: string,
  team1: [string, string],
  team2: [string, string]
): OfflineGameState => {
  const deck = buildShuffledDeck();
  const takeFour = (): CardDto[] => deck.splice(0, 4);

  const hands: Record<string, CardDto[][]> = {};
  const players = [...team1, ...team2];
  players.forEach(p => { hands[p] = []; });

  for (let round = 0; round < TOTAL_ROUNDS; round++) {
    for (const team of [team1, team2]) {
      hands[team[0]].push(takeFour());
      hands[team[1]].push(takeFour());
    }
  }

  const middle = takeFour();

  const remainingHand: Record<string, CardDto[]> = {};
  players.forEach(p => { remainingHand[p] = [...hands[p][0]]; });

  return {
    gameId,
    team1,
    team2,
    turnOrder: [team1[0], team2[0], team1[1], team2[1]],
    currentTurnIndex: 0,
    round: 1,
    roundTurn: 1,
    middle,
    hands,
    remainingHand,
    scoredPiles: { team1: [], team2: [] }
  };
};

export const getTeamKey = (state: OfflineGameState, playerName: string): 'team1' | 'team2' =>
  state.team1.includes(playerName) ? 'team1' : 'team2';

export const getCurrentPlayer = (state: OfflineGameState): string =>
  state.turnOrder[state.currentTurnIndex];

export const getHandForCurrentRound = (state: OfflineGameState, playerName: string): CardDto[] =>
  state.remainingHand[playerName];

export const cardsMatch = (a: CardDto, b: CardDto): boolean => a.rank === b.rank;

/** Would throwing this card onto the current middle capture the pile? (ZNK or SLICE) */
export const wouldCapture = (state: OfflineGameState, card: CardDto): boolean => {
  if (state.middle.length === 0) return false;
  const top = state.middle[state.middle.length - 1];
  if (cardsMatch(top, card)) return true;
  if (card.rank === 'J') return true;
  return false;
};

const advanceRoundState = (state: OfflineGameState): RoundState => {
  state.roundTurn += 1;
  if (state.roundTurn > TURNS_PER_ROUND) {
    state.roundTurn = 1;
    state.round += 1;
    if (state.round > TOTAL_ROUNDS) {
      giveMiddleCardsToLastSlice(state);
      return 'GAME_OVER';
    }
    // pre-dealt hands: move each player onto their next round's hand
    const players = [...state.team1, ...state.team2];
    players.forEach(p => { state.remainingHand[p] = [...state.hands[p][state.round - 1]]; });
    return 'ROUND_FINISHED';
  }
  return 'TURN_FINISHED';
};

const giveMiddleCardsToLastSlice = (state: OfflineGameState): void => {
  if (state.middle.length === 0) return;
  const lastOf = (piles: ScoredPile[]): ScoredPile =>
    piles.length === 0
      ? { round: 0, turn: 0, cards: [] }
      : piles.reduce((latest, p) =>
          p.round > latest.round || (p.round === latest.round && p.turn > latest.turn) ? p : latest
        );
  const last1 = lastOf(state.scoredPiles.team1);
  const last2 = lastOf(state.scoredPiles.team2);
  const winner: 'team1' | 'team2' =
    last1.round !== last2.round
      ? (last1.round > last2.round ? 'team1' : 'team2')
      : (last1.turn >= last2.turn ? 'team1' : 'team2');
  state.scoredPiles[winner].push({ round: 3, turn: 16, cards: state.middle });
  state.middle = [];
};

export const throwCard = (state: OfflineGameState, playerName: string, card: CardDto): ThrowCardResponse => {
  if (getCurrentPlayer(state) !== playerName) {
    throw new Error(`NotYourTurn: it is ${getCurrentPlayer(state)}'s turn, not ${playerName}'s`);
  }
  const hand = state.remainingHand[playerName];
  const idx = hand.findIndex(c => c.rank === card.rank && c.suit === card.suit);
  if (idx === -1) {
    throw new Error(`Card already thrown or not in hand: ${card.rank} of ${card.suit}`);
  }
  hand.splice(idx, 1);

  const team = getTeamKey(state, playerName);
  let effect: PlayerEffect;

  if (state.middle.length > 0) {
    const top = state.middle[state.middle.length - 1];

    if (state.middle.length === 1 && cardsMatch(top, card)) {
      state.middle.push(card);
      state.scoredPiles[team].push({ round: state.round, turn: state.roundTurn, cards: state.middle });
      state.middle = [];
      effect = 'ZNK';
    } else if (cardsMatch(top, card)) {
      state.middle.push(card);
      state.scoredPiles[team].push({ round: state.round, turn: state.roundTurn, cards: state.middle });
      state.middle = [];
      effect = 'SLICE';
    } else if (card.rank === 'J') {
      state.middle.push(card);
      state.scoredPiles[team].push({ round: state.round, turn: state.roundTurn, cards: state.middle });
      state.middle = [];
      effect = 'SLICE';
    } else {
      state.middle.push(card);
      effect = 'APPEND';
    }
  } else {
    state.middle.push(card);
    effect = 'APPEND';
  }

  state.currentTurnIndex = (state.currentTurnIndex + 1) % 4;
  const roundState = advanceRoundState(state);

  return {
    cardDto: card,
    playerEffect: effect,
    roundState,
    thrownByPlayer: playerName,
    nextPlayer: getCurrentPlayer(state),
    autoThrown: false
  };
};

const scoreTeam = (piles: ScoredPile[]): TeamScore => {
  const result: TeamScore = { score: 0, totalCards: 0, znkCount: 0, has2ofClub: false, has10ofDiamond: false };
  for (const pile of piles) {
    result.totalCards += pile.cards.length;
    if (pile.cards.length === 2 && pile.cards[0].rank === pile.cards[1].rank) {
      result.score += 10;
      result.znkCount += 1;
    }
    for (const card of pile.cards) {
      if (card.rank === 'J') result.score += 1;
      if (card.rank === 'A') result.score += 1;
      if (card.rank === '10' && card.suit === 'diamond') {
        result.score += 2;
        result.has10ofDiamond = true;
      }
      if (card.rank === '2' && card.suit === 'club') {
        result.score += 2;
        result.has2ofClub = true;
      }
    }
  }
  if (result.totalCards > 26) result.score += 4;
  return result;
};

export const computeScore = (state: OfflineGameState): GameScore => {
  const team1Score = scoreTeam(state.scoredPiles.team1);
  const team2Score = scoreTeam(state.scoredPiles.team2);
  let winner: string;
  if (team1Score.score > team2Score.score) winner = 'team1';
  else if (team2Score.score > team1Score.score) winner = 'team2';
  else winner = 'DRAW';
  return { team1Score, team2Score, gameId: state.gameId, winner };
};

export const isBot = (playerName: string): boolean => playerName.includes('#BOT#');

export type BotDifficulty = 'easy' | 'medium' | 'hard';

/** Points a single card is worth once it lands in a scored pile (mirrors scoreTeam). */
const cardPointValue = (card: CardDto): number => {
  let value = 0;
  if (card.rank === 'J') value += 1;
  if (card.rank === 'A') value += 1;
  if (card.rank === '10' && card.suit === 'diamond') value += 2;
  if (card.rank === '2' && card.suit === 'club') value += 2;
  return value;
};

/** How many points capturing the current middle pile with this card would score. */
const captureValue = (state: OfflineGameState, card: CardDto): number => {
  if (!wouldCapture(state, card)) return 0;
  const pile = [...state.middle, card];
  let value = pile.reduce((sum, c) => sum + cardPointValue(c), 0);
  if (pile.length === 2 && cardsMatch(pile[0], pile[1])) value += 10; // ZNK bonus
  return value;
};

/** Highest-value capturing card in hand, or null if nothing in hand can capture. */
const bestCapturingCard = (state: OfflineGameState, hand: CardDto[]): CardDto | null => {
  const capturing = hand.filter(card => wouldCapture(state, card));
  if (capturing.length === 0) return null;
  return capturing.reduce((best, card) => (captureValue(state, card) > captureValue(state, best) ? card : best));
};

/**
 * Same as bestCapturingCard, but on a tied score prefers not to spend a J: any J captures
 * any pile, so it's worth holding onto for a bigger pile later rather than burning it here.
 */
const bestCapturingCardSmart = (state: OfflineGameState, hand: CardDto[]): CardDto | null => {
  const capturing = hand.filter(card => wouldCapture(state, card));
  if (capturing.length === 0) return null;
  return capturing.reduce((best, card) => {
    const cv = captureValue(state, card);
    const bv = captureValue(state, best);
    if (cv !== bv) return cv > bv ? card : best;
    if (best.rank === 'J' && card.rank !== 'J') return card;
    return best;
  });
};

/**
 * Cards already publicly accounted for: everything scored by either team, whatever is
 * currently on the table, and the bot's own hand. Used to count how many copies of a rank
 * could still be hiding in another player's hand.
 */
const publiclyKnownCards = (state: OfflineGameState, hand: CardDto[]): CardDto[] => [
  ...state.middle,
  ...state.scoredPiles.team1.flatMap(pile => pile.cards),
  ...state.scoredPiles.team2.flatMap(pile => pile.cards),
  ...hand
];

/** Pick a non-capturing card to throw away, weighing point value against future capture risk. */
const safestDumpCard = (state: OfflineGameState, hand: CardDto[], difficulty: BotDifficulty): CardDto => {
  if (difficulty === 'medium') {
    // Prefer to keep scoring cards (J/A/10♦/2♣) in hand rather than gift them to the table.
    const worthless = hand.filter(card => cardPointValue(card) === 0);
    return (worthless.length > 0 ? worthless : hand)[0];
  }

  // hard: card-counting. A rank with 0 "live" copies left can never be matched and captured
  // again, so it's a free/safe throw; J is never safe to lead since any future J takes it.
  const known = publiclyKnownCards(state, hand);
  const liveCount = (rank: string): number => 4 - known.filter(card => card.rank === rank).length;

  return hand.reduce((safest, card) => {
    const risk = (candidate: CardDto) =>
      (candidate.rank === 'J' ? 99 : liveCount(candidate.rank)) + cardPointValue(candidate) * 2;
    return risk(card) < risk(safest) ? card : safest;
  });
};

/**
 * Bot move selection.
 * - easy: throws a random card, capture opportunities included.
 * - medium: captures the pile for the most points when it can, otherwise dumps a low-value card.
 * - hard: same capturing priority but smarter about which card to capture with (saves Js for
 *   bigger piles) and about card counting on the discard so it doesn't hand the opponents an
 *   easy match later.
 */
export const pickBotCard = (
  state: OfflineGameState,
  playerName: string,
  difficulty: BotDifficulty = 'medium'
): CardDto => {
  const hand = state.remainingHand[playerName];

  if (difficulty === 'easy') {
    return hand[Math.floor(Math.random() * hand.length)];
  }

  if (difficulty === 'medium') {
    return bestCapturingCard(state, hand) ?? safestDumpCard(state, hand, 'medium');
  }

  return bestCapturingCardSmart(state, hand) ?? safestDumpCard(state, hand, 'hard');
};
